import React, { useState } from "react";
import { useTranslation } from "react-i18next";

interface PopupAgreeProps {
  onClose: () => void;
  onAgree: () => void;
}

export const PopupAgree: React.FC<PopupAgreeProps> = ({ onClose, onAgree }) => {
  const { t } = useTranslation();
  const [allChecked, setAllChecked] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [refundChecked, setRefundChecked] = useState(false);

  const handleAllChecked = () => {
    const next = !allChecked;
    setAllChecked(next);
    setTermsChecked(next);
    setRefundChecked(next);
  };

  const handleTermsChecked = () => {
    const next = !termsChecked;
    setTermsChecked(next);
    setAllChecked(next && refundChecked);
  };

  const handleRefundChecked = () => {
    const next = !refundChecked;
    setRefundChecked(next);
    setAllChecked(next && termsChecked);
  };

  const canAgree = termsChecked && refundChecked;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/20">
      <div className="relative isolate flex h-[335px] w-[360px] flex-col items-start gap-6 rounded-2xl bg-white px-5 pb-5 pt-10 shadow-[0_1px_2px_rgba(0,0,0,0.08),0_4px_12px_rgba(0,0,0,0.05)]">
        <button
          className="absolute left-[328px] top-3 z-[3] h-5 w-5 cursor-pointer border-none bg-none p-0"
          onClick={onClose}
          aria-label="Close"
          type="button"
        >
          <svg width={20} height={20} viewBox="0 0 20 20">
            <line
              x1="5"
              y1="5"
              x2="15"
              y2="15"
              stroke="#2B2D2F"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <line
              x1="15"
              y1="5"
              x2="5"
              y2="15"
              stroke="#2B2D2F"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
        {/* Agree to all */}
        <div className="z-0 flex h-[55px] w-full flex-row items-center gap-3 self-stretch rounded-lg bg-[#EAF2FF] p-4">
          <div
            className={`relative flex h-5 w-5 cursor-pointer items-center justify-center rounded-md transition-colors ${allChecked ? "bg-[#007AFF]" : "border border-[#C2C2C2] bg-white"} `}
            onClick={handleAllChecked}
            aria-checked={allChecked}
            role="checkbox"
          >
            {allChecked && (
              <svg
                className="pointer-events-none absolute left-0 top-0 m-[2px]"
                width={16}
                height={16}
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M4 8.5L7 11.5L12 5.5"
                  stroke="#fff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
          <span className="flex h-[23px] items-end text-[15px] font-semibold leading-[22px] text-[#434343]">
            {t("Agree to all")}
          </span>
        </div>
        {/* Info and checkboxes */}
        <div className="z-[1] flex h-[104px] w-full flex-col items-start gap-5 self-stretch px-4">
          <div className="flex h-6 w-[264px] flex-col items-start justify-center gap-2 self-stretch">
            <span className="h-6 w-[264px] text-[16px] font-medium leading-6 text-[#232323]">
              {t("Please check before purchase")}
            </span>
          </div>
          <div className="flex h-5 w-[264px] flex-row items-center gap-2 self-stretch">
            <div
              className={`relative flex h-4 w-4 cursor-pointer items-center justify-center rounded-md transition-colors ${termsChecked ? "bg-[#007AFF]" : "border border-[#C2C2C2] bg-white"} `}
              onClick={handleTermsChecked}
              aria-checked={termsChecked}
              role="checkbox"
            >
              {termsChecked && (
                <svg
                  className="pointer-events-none absolute left-0 top-0"
                  width={16}
                  height={16}
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M4 8.5L7 11.5L12 5.5"
                    stroke="#fff"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
            <span className="flex h-5 items-center text-[14px] font-normal leading-[140%] tracking-[-0.02em] text-[#232323]">
              {t("I agree with the terms and conditions.")}
            </span>
          </div>
          <div className="flex h-5 w-[245px] flex-row items-center gap-2">
            <div
              className={`relative flex h-4 w-4 min-w-4 cursor-pointer items-center justify-center rounded-md transition-colors ${refundChecked ? "bg-[#007AFF]" : "border border-[#C2C2C2] bg-white"} `}
              onClick={handleRefundChecked}
              aria-checked={refundChecked}
              role="checkbox"
            >
              {refundChecked && (
                <svg
                  className="pointer-events-none absolute left-0 top-0"
                  width={16}
                  height={16}
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M4 8.5L7 11.5L12 5.5"
                    stroke="#fff"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
            <span className="flex h-5 items-center text-[14px] font-normal leading-[140%] tracking-[-0.02em] text-[#232323]">
              {t("I agree with the refund/cancel policy.")}
            </span>
          </div>
        </div>
        {/* Button */}
        <div className="z-[2] flex h-12 w-full flex-row items-start gap-4 self-stretch">
          <button
            className={`flex h-12 w-full flex-row items-center justify-center rounded-full border-none px-3 text-center text-[16px] font-medium leading-[19px] ${canAgree ? "cursor-pointer bg-[#A8C6FF]" : "cursor-not-allowed bg-[#E0E0E0]"} `}
            disabled={!canAgree}
            onClick={onAgree}
            type="button"
          >
            <span className="flex h-[19px] w-[59px] items-center justify-center">
              {t("Confirm")}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PopupAgree;
